import { PrivateAgentChatModelSelectionSchema } from "@salesko/contracts";
import type {
  PersonalAgentDevice,
  PrivateAgentProfile,
  PrivateAgentPlacement,
  PrivateAgentMessage,
  PrivateAgentReadiness,
  PrivateAgentTurn,
  PrivateAgentChatRecovery
} from "@salesko/contracts";
import { AgentAvatar, Banner, Button, EmptyState, Field, Spinner, StatusDot } from "@salesko/design-system";
import { CircleAlert, ListTodo, Loader2, MessageSquare, Send, X } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useI18n, type Locale } from "../../i18n";
import { AgentResearchApiError } from "../../lib/api";
import {
  privateAgentChatOwnerKey,
  type PrivateAgentChatProgress,
  type PrivateAgentChatState,
  usePrivateAgentChat
} from "../hooks/usePrivateAgentChat";
import type { PrivateAgentWorkspaceFacet } from "../navItems";
import { WorkspaceSidePanel } from "./WorkspaceSidePanel";
import { useWorkspaceMediaQuery } from "../hooks/useIsMobile";
import { PrivateAgentSelector, privateAgentSelectionCopy } from "./PrivateAgentSelector";
import {
  type PrivateAgentComputerAvailability,
  type PrivateAgentJobsPanelProps,
  type PrivateAgentJobsPresentationPolicy,
  PrivateAgentJobsPanel
} from "./PrivateAgentJobsPanel";

type Copy = ReturnType<typeof useI18n>["t"];

type PrivateAgentWorkspaceProps = PrivateAgentJobsPanelProps & {
  detailVisible: boolean;
  agentId: string | undefined;
  agents: PrivateAgentProfile[];
  placement: PrivateAgentPlacement | null;
  onAgentChange: (agentId: string | undefined) => void;
  agentError: unknown;
  onRefreshAgents: () => void;
  conversationId: string | undefined;
  devices: PersonalAgentDevice[];
  facet: PrivateAgentWorkspaceFacet;
  onConversationChange: (conversationId: string | undefined, agentId?: string) => void;
  onFacetChange: (facet: PrivateAgentWorkspaceFacet) => void;
  /**
   * The server's chat admission verdict, or null while it has not been read.
   * `chat === "ready"` permits first input. Existing conversations
   * accept queued input independently; the Host arbitrates capacity and execution.
   */
  readiness: PrivateAgentReadiness | null;
};

export type PrivateAgentWorkspaceViewProps = PrivateAgentWorkspaceProps & {
  chat: PrivateAgentChatState;
};

function computerStateLabel(computerAvailability: PrivateAgentComputerAvailability, t: Copy) {
  return t.privateAgentWorkspace.computerState[computerAvailability];
}

/**
 * `missing` is not a liveness reading, so it has no dot. The header only shows
 * the dot for the three states the shared control models.
 */
function computerDotStatus(
  computerAvailability: PrivateAgentComputerAvailability
): "online" | "offline" | "revoked" | null {
  return computerAvailability === "missing" ? null : computerAvailability;
}

function turnLabel(turn: Pick<PrivateAgentTurn, "status">, t: Copy) {
  return turn.status === "ended" ? t.privateAgentWorkspace.recovery.ended : t.privateAgentWorkspace.turnStatus[turn.status];
}

/**
 * The recorded observation is a kind and nothing else — the device redacts
 * every content field before egress — so each kind maps to one fixed localized
 * string. Nothing here infers, enriches or rewrites what the device said.
 */
function activityLabel(progress: PrivateAgentChatProgress | null, t: Copy) {
  if (!progress) return null;
  switch (progress.kind) {
    case "progress":
      return t.privateAgentWorkspace.progressThinking;
    case "tool_use":
      return t.privateAgentWorkspace.progressUsingTool;
    case "needs_approval":
      return t.privateAgentWorkspace.progressWaitingApproval;
  }
}

function recoveryBaseLabel(row: PrivateAgentChatRecovery["turns"][number], t: Copy, turn: PrivateAgentTurn | undefined) {
  const copy = t.privateAgentWorkspace.recovery;
  const unfinished = !["unprepared", "pending", "withdrawn_before_dispatch", "terminal"].includes(row.executionObservation);
  if (row.settlement?.reason === "answered") return copy.replyDelivered + (unfinished ? ` · ${turn?.execution?.cancel ? copy.stopPending : copy.checking}` : row.executionObservation === "terminal" && row.resourceObservation !== "quiescent-observed" ? ` · ${copy.cleanupUnknown}` : "");
  if (row.settlement?.reason === "ended") return copy.ended + (unfinished ? ` · ${copy.checking}` : "");
  if (row.settlement?.reason === "cancelled_before_accept") return unfinished ? copy.stopPending : row.executionObservation === "withdrawn_before_dispatch" || row.executionObservation === "unprepared" ? copy.withdrawn : t.privateAgentWorkspace.turnStatus.canceled;
  if (row.actions.retry) return copy.notStarted;
  if (row.executionObservation === "unknown") return copy.checking;
  if (row.executionObservation === "terminal") return copy.needsAttention;
  if (row.executionObservation === "unprepared" || row.executionObservation === "pending") return t.privateAgentWorkspace.turnStatus.queued;
  return row.executionObservation === "admitted" ? t.privateAgentWorkspace.turnStatus.running : t.privateAgentWorkspace.turnStatus.dispatching;
}

function recoveryLabel(row: PrivateAgentChatRecovery["turns"][number], t: Copy, turn: PrivateAgentTurn | undefined) {
  const label = recoveryBaseLabel(row, t, turn);
  const message = row.messageDisposition === "held" ? t.privateAgentWorkspace.recovery.messageHeld
    : row.messageDisposition === "refused" ? t.privateAgentWorkspace.recovery.messageRefused : undefined;
  return message ? `${label} · ${message}` : label;
}

function chatErrorLabel(error: unknown, t: Copy) {
  if (error instanceof AgentResearchApiError) {
    switch (error.code) {
      case "private_agent_chat_rollout_paused":
        return t.privateAgentWorkspace.errors.chatPaused;
      case "private_agent_chat_queue_full":
        return t.privateAgentWorkspace.recovery.queueFull;
      case "private_agent_chat_cancel_conflict":
      case "private_agent_chat_end_conflict":
      case "private_agent_chat_retry_conflict":
      case "private_agent_chat_retry_not_ready":
      case "private_agent_chat_stop_send_conflict":
      case "private_agent_chat_request_conflict":
        return t.privateAgentWorkspace.recovery.actionConflict;
      case "private_agent_chat_turn_conflict":
        return t.privateAgentWorkspace.errors.activeConflict;
      case "private_agent_profile_incomplete":
      case "private_agent_profile_not_found":
        return t.privateAgentWorkspace.errors.profileRequired;
      case "private_agent_chat_replay_gap":
        return t.privateAgentWorkspace.errors.replayGap;
      case "private_agent_chat_reconcile_failed":
        return t.privateAgentWorkspace.errors.reconcileFailed;
    }
  }
  return t.privateAgentWorkspace.errors.requestFailed;
}

/**
 * The single notice for a refused chat, rendered from the server's own
 * `blockedReason`. No reason is inferred locally, and a ready verdict has no
 * notice at all.
 */
export function privateAgentReadinessNotice(readiness: PrivateAgentReadiness | null, t: Copy): string | null {
  if (!readiness || readiness.chat === "ready" || !readiness.blockedReason) {
    return null;
  }
  const copy = t.privateAgentWorkspace.readiness;
  switch (readiness.blockedReason) {
    case "agent_upgrade_required":
      return copy.agent_upgrade_required(readiness.installedRelease ?? null);
    default:
      return copy[readiness.blockedReason];
  }
}

/** Which owner action resolves a refusal. `signIn` has no in-surface control. */
export type PrivateAgentBlockedAction = "settings" | "computers" | "signIn";

export type PrivateAgentBlockedPresentation = {
  action: PrivateAgentBlockedAction;
  actionLabel: string;
  body: string;
  title: string;
};

export type PrivateAgentBlockedContext = {
  /** An in-flight turn whose frozen computer is gone from the device list. */
  boundDeviceRevoked: boolean;
  computerAvailability: PrivateAgentComputerAvailability;
  profileMissing: boolean;
  signedOut: boolean;
};

/**
 * Groups the server's refusal into the three situations an owner can act on.
 * The reason itself is never synthesized here: a blocked verdict that names no
 * `blockedReason` produces no presentation at all, exactly like the notice
 * above. Only the grouping and the single call to action are local.
 */
export function privateAgentBlockedPresentation(
  readiness: PrivateAgentReadiness | null,
  context: PrivateAgentBlockedContext,
  t: Copy
): PrivateAgentBlockedPresentation | null {
  const copy = t.privateAgentWorkspace;
  if (context.signedOut) {
    return { action: "signIn", actionLabel: t.common.signIn, body: copy.signIn, title: copy.signInTitle };
  }

  const setup = (body: string): PrivateAgentBlockedPresentation => ({
    action: "settings",
    actionLabel: t.privateAgentJobs.setUpAgent,
    body,
    title: copy.setupTitle
  });
  const computer = (body: string): PrivateAgentBlockedPresentation => ({
    action: "computers",
    actionLabel: copy.openComputers,
    body,
    title: copy.computerOfflineTitle
  });
  const update = (body: string): PrivateAgentBlockedPresentation => ({
    action: "computers",
    actionLabel: copy.openComputers,
    body,
    title: copy.updateTitle
  });

  const blockedReason = readiness && readiness.chat === "blocked" ? readiness.blockedReason ?? null : null;
  if (blockedReason) {
    const reason = copy.readiness;
    switch (blockedReason) {
      case "profile_missing":
        return setup(reason.profile_missing);
      case "profile_identity_unavailable":
        return setup(reason.profile_identity_unavailable);
      case "device_offline":
        return computer(reason.device_offline);
      case "device_missing":
        return computer(reason.device_missing);
      case "device_revoked":
        return computer(reason.device_revoked);
      case "agent_upgrade_required":
        return update(reason.agent_upgrade_required(readiness?.installedRelease ?? null));
      case "runtime_missing":
        return update(reason.runtime_missing);
      case "runtime_not_authenticated":
        return update(reason.runtime_not_authenticated);
      case "toolset_not_configured":
        return update(reason.toolset_not_configured);
      case "runtime_unsupported_for_tools":
        return update(reason.runtime_unsupported_for_tools);
    }
  }

  // A locally observed device-list fact about an in-flight turn, not a reason
  // read off the server. It only speaks when the server named nothing.
  if (context.boundDeviceRevoked) {
    return computer(copy.boundDeviceRevokedNotice);
  }

  // The server answered (ready, or blocked while naming no reason). Nothing
  // local may stand in for a reason it did not give.
  if (readiness) {
    return null;
  }

  // No verdict yet: keep the existing local precondition so the surface neither
  // invents an admission nor blocks on an answer it does not have.
  if (context.profileMissing) {
    return setup(copy.profileRequired);
  }
  if (context.computerAvailability !== "online") {
    return computer(copy.readiness.device_offline);
  }
  return null;
}

/** Seconds since a server timestamp, ticking once a second. Presentation only. */
function useElapsedSeconds(since: string | undefined): number | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!since) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [since]);

  if (!since) return null;
  const started = Date.parse(since);
  if (Number.isNaN(started)) return null;
  return Math.max(0, Math.floor((now - started) / 1000));
}

function formatElapsed(totalSeconds: number): string {
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${seconds}` : `${minutes}:${seconds}`;
}

function calendarDay(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return `${parsed.getFullYear()}-${parsed.getMonth()}-${parsed.getDate()}`;
}

function formatDate(value: string, locale: Locale, dateStyle: "medium" | "short"): string {
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle }).format(new Date(value));
  } catch {
    return value;
  }
}

/**
 * Product composition only: durable Private Agent Chat plus the existing
 * research-jobs authority. No assistant response, runner selection, or
 * progress is derived in the browser.
 */
export function PrivateAgentWorkspace(props: PrivateAgentWorkspaceProps) {
  const chat = usePrivateAgentChat({ agentId: props.agentId, conversationId: props.conversationId, enabled: props.enabled });
  useEffect(() => {
    if (chat.conversation && chat.conversation.conversationId !== props.conversationId)
      props.onConversationChange(chat.conversation.conversationId, chat.conversation.agentId);
  }, [chat.conversation?.conversationId, props.conversationId, props.onConversationChange]);
  return <PrivateAgentWorkspaceView {...props} chat={chat} />;
}

/** Presentation seam for BDD coverage without mocking a Local Agent reply. */
export function PrivateAgentWorkspaceView({
  detailVisible,
  agentId,
  agents,
  placement,
  onAgentChange,
  agentError,
  onRefreshAgents,
  chat,
  conversationId,
  devices,
  facet,
  jobs,
  onConversationChange,
  onFacetChange,
  profile,
  readiness,
  ...panelProps
}: PrivateAgentWorkspaceViewProps) {
  const { locale, t } = useI18n();
  const draftKey = JSON.stringify([privateAgentChatOwnerKey(), agentId, conversationId]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [modelDrafts, setModelDrafts] = useState<Record<string, string>>({});
  const frozenPendingModel = chat.pendingIntent?.kind === "send" ? chat.pendingIntent.request.modelSelection
    : chat.pendingIntent?.kind === "stopAndSend" ? chat.pendingIntent.request.input.modelSelection : undefined;
  const piModel = profile?.runtimePreference === "pi_provider_key";
  const defaultModelText = piModel ? (profile?.providerProfile ? JSON.stringify(profile.providerProfile) : "") : profile?.subscriptionModelId ?? "";
  const modelText = frozenPendingModel ? (frozenPendingModel.lane === "byok-profile" ? JSON.stringify(frozenPendingModel.providerProfile) : frozenPendingModel.modelId)
    : modelDrafts[draftKey] ?? defaultModelText;
  let modelSelection: ReturnType<typeof PrivateAgentChatModelSelectionSchema.safeParse>;
  try {
    modelSelection = PrivateAgentChatModelSelectionSchema.safeParse(piModel
      ? { lane: "byok-profile", runtimeId: "pi", providerProfile: JSON.parse(modelText) }
      : { lane: "subscription", runtimeId: profile?.runtimePreference === "claude_subscription" ? "claude" : profile?.runtimePreference === "codex_subscription" ? "codex" : undefined, providerId: null, modelId: modelText });
  } catch { modelSelection = PrivateAgentChatModelSelectionSchema.safeParse(null); }
  const modelCopy = {
    en: { binding: "Model binding JSON", required: "Choose an explicit model before sending.", reset: "Use Agent default", fixed: "Harness is fixed for this Agent. Model changes apply to future messages." },
    "zh-Hans": { binding: "模型 binding JSON", required: "发送前请明确指定模型。", reset: "使用 Agent 默认模型", fixed: "Agent 的 harness 已固定。模型更改只影响之后发送的消息。" },
    "zh-Hant": { binding: "模型 binding JSON", required: "傳送前請明確指定模型。", reset: "使用 Agent 預設模型", fixed: "Agent 的 harness 已固定。模型更改只影響之後傳送的訊息。" },
    ja: { binding: "モデル binding JSON", required: "送信前にモデルを指定してください。", reset: "Agent の既定モデルを使用", fixed: "Agent の harness は固定されています。変更は今後送信するメッセージに適用されます。" },
  }[locale];
  const modelControls = <div className="private-agent-model-selection">
    <Field label={piModel ? modelCopy.binding : t.privateAgent.modelId} hint={piModel ? undefined : t.privateAgent.subscriptionModelHint} value={modelText} maxLength={piModel ? 4096 : 160}
      disabled={Boolean(chat.pendingIntent) || chat.performing} onChange={value => setModelDrafts(previous => ({ ...previous, [draftKey]: value }))} />
    <Button type="button" size="sm" variant="secondary" disabled={Boolean(chat.pendingIntent) || chat.performing}
      onClick={() => setModelDrafts(previous => { const next = { ...previous }; delete next[draftKey]; return next; })}>{modelCopy.reset}</Button>
    <p>{modelCopy.fixed}</p>
    {!modelSelection.success ? <p role="status">{modelCopy.required}</p> : null}
  </div>;
  const pendingInput = chat.pendingIntent?.kind === "send" ? chat.pendingIntent.request.message
    : chat.pendingIntent?.kind === "stopAndSend" ? chat.pendingIntent.request.input.message : null;
  const draft = panelProps.enabled ? pendingInput ?? drafts[draftKey] ?? "" : "";
  const setDraft = (value: string) => setDrafts(previous => ({ ...previous, [draftKey]: value }));
  useEffect(() => { if (pendingInput !== null) setDrafts(previous => ({ ...previous, [draftKey]: pendingInput })); }, [draftKey, pendingInput]);
  const clearSubmittedDraft = (key: string, body: string) => setDrafts(previous => {
    if ((previous[key] ?? "") !== body) return previous;
    const next = { ...previous }; delete next[key]; return next;
  });
  const railDrawer = useWorkspaceMediaQuery(detailVisible ? "(max-width: 1120px)" : "(max-width: 760px)");
  const [railOpen, setRailOpen] = useState(false);
  useEffect(() => { setRailOpen(false); }, [railDrawer, conversationId, agentId]);
  const selectedConversation = chat.conversation;
  const activeExecutionTurn = chat.activeTurn;
  const executionBinding = activeExecutionTurn?.binding;
  const boundDevice = selectedConversation
    && executionBinding
    ? devices.find((device) => device.deviceId === executionBinding.deviceId)
    : undefined;
  // Only an in-flight turn/cancel surface is governed by its frozen device.
  // An idle conversation must submit to the API, which resolves the current
  // Profile binding rather than treating this historical snapshot as authority.
  const boundDeviceRevoked = Boolean(activeExecutionTurn?.execution) && (!boundDevice || boundDevice.revoked);
  const blocked = privateAgentBlockedPresentation(
    readiness,
    {
      boundDeviceRevoked,
      computerAvailability: panelProps.computerAvailability,
      profileMissing: panelProps.profileMissing,
      signedOut: !panelProps.enabled
    },
    t
  );
  const canCompose = Boolean(agentId) && panelProps.enabled && !agentError && !chat.conversationLoading && !chat.conversationError && (!chat.conversation || chat.conversation.agentId === agentId)
    && chat.intentReady && !chat.performing && !chat.pendingIntent;
  const canSend = canCompose && (!chat.conversation || chat.recovery !== null && chat.recovery.unsettledTurns < 8);
  const presentationPolicy: PrivateAgentJobsPresentationPolicy = {
    showJobId: false,
    targetFallbackLabel: t.privateAgentWorkspace.targetUnavailable
  };
  const profileDevice = placement ? devices.find((device) => device.deviceId === placement.deviceId) : undefined;
  const headerDeviceName = (boundDevice ?? profileDevice)?.deviceName;
  const computerDot = panelProps.enabled ? computerDotStatus(panelProps.computerAvailability) : null;
  const computerState = computerStateLabel(panelProps.computerAvailability, t);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSend) return;
    const source = draftKey, body = draft;
    if (!modelSelection.success) return;
    if (await chat.sendMessage(body, modelSelection.data)) clearSubmittedDraft(source, body);
  };

  const stopAndSend = async (turnId: string) => {
    if (!canCompose) return;
    const source = draftKey, body = draft;
    if (!modelSelection.success) return;
    if (await chat.stopAndSend(turnId, body, modelSelection.data)) clearSubmittedDraft(source, body);
  };
  const retryPending = async () => {
    const source = draftKey, body = pendingInput;
    if (await chat.retryPending() && body !== null) clearSubmittedDraft(source, body);
  };

  const runBlockedAction = (action: PrivateAgentBlockedAction) => {
    if (action === "settings") panelProps.onOpenPrivateAgentSettings();
    if (action === "computers") panelProps.onOpenComputers();
  };

  return (
    <main className={`private-agent-workspace${railDrawer ? " is-rail-collapsed" : ""}`} aria-label={t.privateAgentWorkspace.ariaLabel}>
      <WorkspaceSidePanel className="private-agent-conversations" label={t.privateAgentWorkspace.conversationsAriaLabel}
        drawer={railDrawer} open={!railDrawer || railOpen} onClose={() => setRailOpen(false)}
        id="private-agent-conversations" triggerId="private-agent-conversations-trigger" side="left">
        {railDrawer ? <button type="button" className="icon-button" aria-label={t.common.dismiss} onClick={() => setRailOpen(false)}><X size={17} /></button> : null}
        <PrivateAgentSelector agents={agents} agentId={agentId} onChange={onAgentChange} disabled={!panelProps.enabled || chat.performing} />
        {!agentId ? <p role="status">{privateAgentSelectionCopy[locale].required}</p> : null}
        {agentId && !readiness && !agentError ? <p role="status">{privateAgentSelectionCopy[locale].checking}</p> : null}
        {agentError ? <Banner status="error" action={<Button onClick={onRefreshAgents}>{t.common.retry}</Button>}>{privateAgentSelectionCopy[locale].loadError}</Banner> : null}
        <div className="private-agent-rail-list">
          {chat.conversation ? <button type="button" className="private-agent-conversation is-active" aria-current="page"
            onClick={() => { setRailOpen(false); onConversationChange(chat.conversation!.conversationId, chat.conversation!.agentId); onFacetChange("chat"); }}>
            {chat.conversation.title}
          </button> : null}
          <Button disabled={!chat.conversation || !chat.intentReady || chat.performing || Boolean(chat.pendingIntent)}
            onClick={() => void chat.clearChat()} size="sm" variant="secondary">
            {{en: "Clear chat", "zh-Hans": "清空聊天", "zh-Hant": "清空聊天", ja: "チャットをクリア"}[locale]}
          </Button>
        </div>
      </WorkspaceSidePanel>

      <section className="private-agent-workspace-main">
        <header className="private-agent-workspace-header">
          {railDrawer ? <button type="button" id="private-agent-conversations-trigger" className="icon-button"
            aria-label={t.privateAgentWorkspace.conversationsAriaLabel} aria-controls="private-agent-conversations"
            aria-expanded={railOpen} onClick={() => setRailOpen(true)}><MessageSquare size={19} /></button> : null}
          <span className="private-agent-workspace-avatar" aria-hidden="true">
            <AgentAvatar size="md" variant="private" />
          </span>
          <div className="private-agent-workspace-identity">
            <div className="private-agent-identity-name">
              <h1>{profile?.name ?? t.privateAgentJobs.title}</h1>
              {computerDot && !headerDeviceName ? (
                <StatusDot label={computerState} size="sm" status={computerDot} />
              ) : null}
            </div>
            {computerDot && headerDeviceName ? (
              <p className="private-agent-identity-device">
                <StatusDot size="sm" srLabel={computerState} status={computerDot} />
                <span>{t.privateAgentWorkspace.deviceLine(headerDeviceName, computerState)}</span>
              </p>
            ) : null}
            <p>{t.privateAgentWorkspace.identitySubtitle}</p>
          </div>
        </header>

        <div className="private-agent-workspace-facets" role="tablist" aria-label={t.privateAgentWorkspace.facetsAriaLabel}>
          {(["chat", "research"] as const).map((candidate) => {
            const Icon = candidate === "chat" ? MessageSquare : ListTodo;
            const selected = facet === candidate;
            return (
              <button
                aria-controls={`private-agent-${candidate}`}
                aria-selected={selected}
                className={selected ? "is-active" : undefined}
                key={candidate}
                onClick={() => onFacetChange(candidate)}
                role="tab"
                type="button"
              >
                <Icon aria-hidden="true" size={17} />
                {candidate === "chat" ? t.privateAgentWorkspace.chat : t.privateAgentWorkspace.research}
              </button>
            );
          })}
        </div>

        <section className="private-agent-workspace-content" id={`private-agent-${facet}`} role="tabpanel">
          {facet === "chat" ? (
            <PrivateAgentChatPanel
              agentName={profile?.name ?? t.privateAgentWorkspace.agent}
              blocked={blocked}
              canSend={canSend && modelSelection.success}
              canCompose={canCompose}
              onStopAndSend={stopAndSend}
              onRetryPending={retryPending}
              chat={chat}
              draft={draft}
              locale={locale}
              onBlockedAction={runBlockedAction}
              modelControls={modelControls}
              modelValid={modelSelection.success}
              onDraftChange={setDraft}
              onSubmit={submit}
              t={t}
            />
          ) : (
            <PrivateAgentJobsPanel
              {...panelProps}
              emptyState={{
                description: t.privateAgentWorkspace.researchEmpty,
                title: t.privateAgentWorkspace.researchEmptyTitle
              }}
              jobs={jobs}
              profile={profile}
              presentationPolicy={presentationPolicy}
              showHeader={false}
            />
          )}
        </section>
      </section>
    </main>
  );
}

function PrivateAgentChatPanel({
  agentName,
  modelControls,
  modelValid,
  blocked,
  canSend,
  canCompose,
  onStopAndSend,
  onRetryPending,
  chat,
  draft,
  locale,
  onBlockedAction,
  onDraftChange,
  onSubmit,
  t
}: {
  agentName: string;
  modelControls: ReactNode;
  modelValid: boolean;
  blocked: PrivateAgentBlockedPresentation | null;
  canSend: boolean;
  canCompose: boolean;
  onStopAndSend: (turnId: string) => Promise<void>;
  onRetryPending: () => Promise<void>;
  chat: PrivateAgentChatState;
  draft: string;
  locale: Locale;
  onBlockedAction: (action: PrivateAgentBlockedAction) => void;
  onDraftChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  t: Copy;
}) {
  const activeTurn = chat.activeTurn;
  const progressLabel = activityLabel(chat.activeProgress, t);
  const elapsedSeconds = useElapsedSeconds(activeTurn ? activeTurn.startedAt ?? activeTurn.createdAt : undefined);
  const endRef = useRef<HTMLDivElement | null>(null);
  const conversationId = chat.conversation?.conversationId;
  const messageCount = chat.messages.length;
  const activeTurnStatus = chat.activeTurn?.status ?? null;
  const latestTurnStatus = chat.turns[0]?.status ?? null;
  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: "end" });
  }, [conversationId, messageCount, activeTurnStatus, latestTurnStatus]);
  const visibleRecovery = chat.recovery ? [...new Map([...chat.recovery.turns, ...chat.recovery.executions].map(row => [row.turnId, row])).values()].filter(row => row.settlement === null || row.actions.stop
    || !["unprepared", "pending", "withdrawn_before_dispatch", "terminal"].includes(row.executionObservation)
    || row.turnSequence === Math.max(...chat.recovery!.turns.map(item => item.turnSequence)))
    .sort((left, right) => left.turnSequence - right.turnSequence) : [];
  const postHocAlerts = chat.events.filter(event => event.type === "turn.budget_bound_falsified"
    || event.type === "turn.budget_integrity_mismatch_observed"
    || event.type === "turn.context_overflow_observed" || event.type === "turn.usage_unavailable_observed");
  const postHocCopy = {
    en: { title: "Context budget warning", falsified: "The measured prompt exceeded its admitted bound. New messages are paused until the budget ruling is updated.",
      overflow: "This turn reported context overflow. Its accepted reply may have missed earlier context.",
      usage: "This turn had no usable prompt count. Its context coverage could not be verified.",
      integrity: "The terminal usage did not match the prepared request. Its budget result could not be verified." },
    "zh-Hans": { title: "上下文预算警告", falsified: "实测输入超出了准入上界。预算裁定更新前，新消息将暂停。",
      overflow: "这一轮报告了上下文溢出。已接受的回复可能遗漏了较早的上下文。",
      usage: "这一轮没有可用的输入计数，无法核实上下文覆盖范围。",
      integrity: "终结回执与已准备的请求不一致，无法核实这一轮的预算结果。" },
    "zh-Hant": { title: "上下文預算警告", falsified: "實測輸入超出了准入上界。預算裁定更新前，新訊息將暫停。",
      overflow: "這一輪回報了上下文溢出。已接受的回覆可能遺漏較早的上下文。",
      usage: "這一輪沒有可用的輸入計數，無法核實上下文涵蓋範圍。",
      integrity: "終結回執與已準備的請求不一致，無法核實這一輪的預算結果。" },
    ja: { title: "コンテキスト予算の警告", falsified: "実測入力が許容上限を超えました。予算規則の更新まで新しいメッセージは停止します。",
      overflow: "このターンでコンテキスト超過が報告されました。受理済みの返信には以前の文脈が含まれない可能性があります。",
      usage: "このターンの入力トークン数を取得できず、文脈の網羅性を確認できません。",
      integrity: "終了レシートが準備済みのリクエストと一致せず、予算結果を確認できません。" },
  }[locale];
  const blockedAction =
    blocked && blocked.action !== "signIn" ? (
      <Button onClick={() => onBlockedAction(blocked.action)} size="sm" variant="secondary">
        {blocked.actionLabel}
      </Button>
    ) : null;

  if (!chat.conversation && blocked) return <div className="private-agent-chat-panel">
    {chat.conversationError ? <Banner status="error">{chatErrorLabel(chat.conversationError, t)}</Banner> : null}
    <EmptyState action={blockedAction} icon={<CircleAlert size={20} />} title={blocked.title}>{blocked.body}</EmptyState>
  </div>;

  return (
    <div className="private-agent-chat-panel">
      {chat.conversationLoading ? <Spinner label={t.privateAgentWorkspace.loadingConversation} size={16} /> : null}
      {chat.conversationError ? (
        <Banner action={<Button onClick={chat.refresh} size="sm" variant="secondary">{t.common.retry}</Button>} status="error">
          {chatErrorLabel(chat.conversationError, t)}
        </Banner>
      ) : null}
      {postHocAlerts.map(event => <Banner key={event.eventId} status="warning" title={postHocCopy.title}>
        {event.type === "turn.budget_bound_falsified" ? postHocCopy.falsified
          : event.type === "turn.budget_integrity_mismatch_observed" ? postHocCopy.integrity
          : event.type === "turn.context_overflow_observed" ? postHocCopy.overflow : postHocCopy.usage}
      </Banner>)}
      {chat.messages.length === 0 && !chat.conversationLoading ? (
        <p className="private-agent-chat-empty">{t.privateAgentWorkspace.emptyTranscript}</p>
      ) : null}
      {chat.hasMoreMessages ? <Button disabled={chat.loadingOlder} onClick={chat.loadOlder} variant="secondary">
        {{en: "Load earlier messages", "zh-Hans": "加载更早消息", "zh-Hant": "載入更早訊息", ja: "以前のメッセージを読み込む"}[locale]}
      </Button> : null}
      <ol className="private-agent-transcript" aria-label={t.privateAgentWorkspace.transcriptAriaLabel}>
        {chat.messages.map((message, index) => {
          const previous = index > 0 ? chat.messages[index - 1] : undefined;
          const dayChanged = previous !== undefined && calendarDay(previous.createdAt) !== calendarDay(message.createdAt);
          return (
            <TranscriptMessage
              agentName={agentName}
              dateDivider={dayChanged ? formatDate(message.createdAt, locale, "medium") : null}
              key={message.messageId}
              message={message}
              t={t}
            />
          );
        })}
      </ol>
      <div className="private-agent-footer" ref={endRef}>
        {chat.eventsError ? (
          <Banner action={<Button onClick={chat.refresh} size="sm" variant="secondary">{t.common.retry}</Button>} status="error">
            {chatErrorLabel(chat.eventsError, t)}
          </Banner>
        ) : null}
        {chat.recovery ? <section aria-label={t.privateAgentWorkspace.recovery.queueLabel} className="private-agent-recovery">
          <p>{t.privateAgentWorkspace.recovery.queueCount(chat.recovery.unsettledTurns)}</p>
          <ul>
            {visibleRecovery.map(row => <li key={row.turnId} data-turn-id={row.turnId}>
              <span role="status">#{row.turnSequence} · {recoveryLabel(row, t, chat.turns.find(turn => turn.turnId === row.turnId))}</span>
              {row.turnId === activeTurn?.turnId && !row.settlement && progressLabel ? <span className="private-agent-turn-progress">{progressLabel}</span> : null}
              {row.turnId === activeTurn?.turnId && row.executionObservation === "admitted" && elapsedSeconds !== null
                ? <span className="private-agent-turn-elapsed">{t.privateAgentWorkspace.elapsed(formatElapsed(elapsedSeconds))}</span> : null}
              <div className="private-agent-turn-state">
                {(["stop", "end", "retry"] as const).filter(action => row.actions[action]).map(action =>
                  <Button key={action} disabled={!canCompose} onClick={() => void chat.executeAction(action, row.turnId)} size="sm" variant="secondary">
                    {action === "stop" ? t.privateAgentWorkspace.cancelTurn : action === "end" ? t.privateAgentWorkspace.recovery.endTurn : t.privateAgentWorkspace.recovery.retryTurn}
                  </Button>)}
                {row.actions.stopAndSend ? <Button disabled={!canCompose || !modelValid || !draft.trim()} onClick={() => void onStopAndSend(row.turnId)} size="sm" variant="secondary">
                  {t.privateAgentWorkspace.recovery.stopAndSend}
                </Button> : null}
                {row.execution && row.executionObservation !== "terminal" && row.executionObservation !== "withdrawn_before_dispatch"
                  ? <Button onClick={() => chat.reconcileNow(row.turnId)} size="sm" variant="ghost">{t.privateAgentWorkspace.checkExecution}</Button> : null}
              </div>
            </li>)}
          </ul>
        </section> : null}
        {chat.pendingIntent ? <Banner status="warning" action={<Button disabled={chat.performing || !chat.intentReady} onClick={() => void onRetryPending()} size="sm" variant="secondary">{t.privateAgentWorkspace.recovery.retryPending}</Button>}>
          {t.privateAgentWorkspace.recovery.pendingRequest}
        </Banner> : null}
        {chat.intentError ? <Banner status="error" action={!chat.intentReady ? <Button onClick={chat.refresh} size="sm" variant="secondary">{t.common.retry}</Button> : undefined}>
          {chat.intentReady ? chatErrorLabel(chat.intentError, t) : t.privateAgentWorkspace.recovery.storageFailure}
        </Banner> : null}
        {chat.recovery?.unsettledTurns === 8 ? <p role="status">{t.privateAgentWorkspace.recovery.queueFull}</p> : null}
        {chat.reconcileError ? (
          <Banner action={<Button onClick={() => chat.reconcileNow()} size="sm" variant="secondary">{t.privateAgentWorkspace.checkExecution}</Button>} status="error">
            {chatErrorLabel(chat.reconcileError, t)}
          </Banner>
        ) : null}
        {blocked ? (
          <Banner action={blockedAction} status="warning">
            {blocked.body}
          </Banner>
        ) : null}
        <form className="private-agent-composer" onSubmit={onSubmit}>
          {modelControls}
          <div className="agent-input-shell private-agent-input-shell">
            <label className="sr-only" htmlFor="private-agent-message">{t.privateAgentWorkspace.composerLabel}</label>
            <textarea
              id="private-agent-message"
              disabled={!canCompose}
              onChange={(event) => onDraftChange(event.target.value)}
              placeholder={t.privateAgentWorkspace.composerPlaceholder}
              value={draft}
            />
            <div className="agent-composer-actions private-agent-composer-actions">
              <button
                aria-label={chat.sending ? t.privateAgentWorkspace.sending : t.privateAgentWorkspace.send}
                className="agent-send-button"
                disabled={!canSend || draft.trim().length === 0}
                title={chat.sending ? t.privateAgentWorkspace.sending : t.privateAgentWorkspace.send}
                type="submit"
              >
                {chat.sending ? <Loader2 className="spin" size={17} /> : <Send size={17} />}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function TranscriptMessage({
  agentName,
  dateDivider,
  message,
  t
}: {
  agentName: string;
  dateDivider: string | null;
  message: PrivateAgentMessage;
  t: Copy;
}) {
  const assistant = message.role === "assistant";
  return (
    <>
      {dateDivider ? <li className="private-agent-date-divider">{dateDivider}</li> : null}
      <li className={assistant ? "private-agent-message is-assistant" : "private-agent-message is-user"}>
        <span className="private-agent-message-avatar" aria-hidden="true">
          {assistant ? <AgentAvatar size="sm" variant="private" /> : t.privateAgentWorkspace.youInitial}
        </span>
        <div>
          <div className="private-agent-message-meta">
            <strong>{assistant ? agentName : t.privateAgentWorkspace.you}</strong>
            <time dateTime={message.createdAt}>{new Date(message.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</time>
          </div>
          <div className="private-agent-message-body">
            {assistant ? (
              <ReactMarkdown
                components={{
                  // Only href/title/children are carried over: spreading the
                  // plugin's own props would drag in a second React typings
                  // realm, and a markdown link must not set anything else.
                  a: ({ children, href, title }) => (
                    <a href={href} rel="noreferrer noopener" target="_blank" title={title}>
                      {children}
                    </a>
                  ),
                  // The scroll container lives outside the table so the table
                  // itself can stay display:table and stretch to the column.
                  table: ({ children }) => (
                    <div className="private-agent-table-scroll">
                      <table>{children}</table>
                    </div>
                  )
                }}
                remarkPlugins={[remarkGfm]}
              >
                {message.content}
              </ReactMarkdown>
            ) : (
              <p>{message.content}</p>
            )}
          </div>
        </div>
      </li>
    </>
  );
}
